import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const LeagueTile = ({ league, leagueDetails }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/leagues/${league.id}`);
  };

  // Format season display
  const formatSeason = (season) => {
    return season ? `${season} Season` : 'Current Season';
  };

  // Get status color based on league status
  const getStatusColor = (status) => {
    switch (status) {
      case 'in_season':
        return colors.greenAccent[500];
      case 'complete':
        return colors.blueAccent[500];
      case 'pre_draft':
        return colors.yellowAccent[500];
      default:
        return colors.grey[500];
    }
  };

  // Format status text
  const formatStatus = (status) => {
    switch (status) {
      case 'in_season':
        return 'In Season';
      case 'complete':
        return 'Complete';
      case 'pre_draft':
        return 'Pre-Draft';
      default:
        return 'Unknown';
    }
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.primary[400],
        borderRadius: 2,
        border: `1px solid ${colors.grey[700]}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${alpha(colors.blueAccent[500], 0.3)}`,
          borderColor: colors.blueAccent[500],
          backgroundColor: alpha(colors.primary[400], 0.8),
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* Header with avatar and basic info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={leagueDetails?.avatar ? `https://sleepercdn.com/avatars/thumbs/${leagueDetails.avatar}` : null}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: colors.blueAccent[500],
              mr: 2,
            }}
          >
            <EmojiEventsIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.grey[100],
                fontWeight: 600,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {leagueDetails?.name || league.name || 'Unnamed League'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.875rem',
              }}
            >
              {formatSeason(leagueDetails?.season)}
            </Typography>
          </Box>
        </Box>

        {/* League details */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Status and roster count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {leagueDetails?.status && (
              <Chip
                label={formatStatus(leagueDetails.status)}
                size="small"
                sx={{
                  backgroundColor: alpha(getStatusColor(leagueDetails.status), 0.2),
                  color: getStatusColor(leagueDetails.status),
                  borderColor: getStatusColor(leagueDetails.status),
                  border: `1px solid`,
                  fontWeight: 600,
                }}
              />
            )}
            {leagueDetails?.total_rosters && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon sx={{ fontSize: 16, color: colors.grey[400] }} />
                <Typography
                  variant="body2"
                  sx={{ color: colors.grey[300], fontSize: '0.875rem' }}
                >
                  {leagueDetails.total_rosters} Teams
                </Typography>
              </Box>
            )}
          </Box>

          {/* League ID for reference */}
          <Box sx={{ mt: 'auto' }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[500],
                fontSize: '0.75rem',
                fontFamily: 'monospace',
              }}
            >
              ID: {league.external_league_id}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LeagueTile;
